/** @format */

import { trackEvent } from "./ga";

// GA4 events for the "See It in Your Room" funnel, from the first click to
// the purchase:
//
//   visualizer_cta_click     { location }  a "Try It in My Room" / nav / home /
//                                          shop / blackout link was clicked
//   visualizer_intro_view                  the landing page was shown
//   visualizer_session_start { account }   the tool opened (always "customer")
//   visualizer_photo_upload                a room photo reached the tool
//   visualizer_preview                     a shade was first drawn on that photo
//   visualizer_customize                   the shopper changed a shade's options
//   add_to_cart (item_list_name "visualizer") and visualizer_add_to_cart
//   purchase (items carry item_list_name "visualizer") and visualizer_purchase
//
// The photo, preview and customize steps happen inside the framed tool; this
// page sees them in the `sgv:state` messages the frame posts (see
// screens/visualizer), via `progressEvents` below.

/**
 * Where the "Try It in My Room" buttons outside the landing page go: the
 * tool at once when logged in, else the login page and back.
 */
export const VISUALIZER_START_PATH = "/visualizer?start=1";

export const trackVisualizerCta = (location) =>
	trackEvent("visualizer_cta_click", { location });

/** What a shade's options are, as one comparable string. */
const shadeKey = (shade) =>
	shade
		? [
				shade.product,
				shade.fabricCode,
				shade.operation,
				shade.position,
				shade.valance,
				shade.valanceColor,
				shade.bottomBar,
				shade.bottomBarColor,
				shade.mount,
				shade.rollType,
				shade.stack,
				shade.remote,
				shade.charger,
				shade.connector,
			].join("|")
		: null;

export const newProgress = () => ({
	photos: new Set(),
	previewed: new Set(), // photo ids with a shade drawn
	shades: new Map(), // window id -> shadeKey
	customized: false,
});

/**
 * The funnel events one `sgv:state` message adds, given what was seen before
 * (`progress`, updated in place). Each photo is counted once as uploaded and
 * once as previewed; customizing is counted once per visit.
 */
export const progressEvents = (progress, state) => {
	const events = [];
	const photoId = state?.photoId || null;
	const windows = Array.isArray(state?.windows) ? state.windows : [];
	if (photoId && !progress.photos.has(photoId)) {
		progress.photos.add(photoId);
		events.push(["visualizer_photo_upload", {}]);
	}
	if (photoId && !progress.previewed.has(photoId) && windows.some((w) => w?.shade)) {
		progress.previewed.add(photoId);
		events.push(["visualizer_preview", { product: windows.find((w) => w?.shade).shade.product }]);
	}
	for (const w of windows) {
		if (!w?.id) continue;
		const key = shadeKey(w.shade);
		const before = progress.shades.get(w.id);
		if (key && before && key !== before && !progress.customized) {
			progress.customized = true;
			events.push(["visualizer_customize", { product: w.shade.product }]);
		}
		if (key) progress.shades.set(w.id, key);
	}
	return events;
};
